/**
 * useVoiceCapture — Web Audio + SpeechRecognition capture for Operation
 * Listen. Per-speaker, per-phone, graceful degradation.
 *
 * Returns { state, start, stop, captureBaseline, error }.
 *
 * state:
 *   'idle'       — permission not requested
 *   'needsBaseline' — mic granted, waiting for baseline sentence
 *   'ready'      — baseline captured, not recording
 *   'recording'  — live capture + batched upload every 10 s
 *   'denied'     — mic denied
 *   'unsupported'— browser lacks Web Audio
 *
 * The hook does NOT render UI. The companion `VoiceCaptureControl` component
 * does.
 *
 * Design doc §3: every 5 s we post one voice sample per actor:
 *   { ts, actor, loudness_db, pitch_hz, activity, overlap }
 * Transcript lines are posted as the recognizer emits them.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

const API = process.env.REACT_APP_BACKEND_URL;
const authHeader = () => {
  const t = localStorage.getItem('gntl_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

// How often we sample loudness/pitch — 5-second cadence per the design doc.
const SAMPLE_INTERVAL_MS = 5000;
// How often we flush sample + transcript batches to the server.
const FLUSH_INTERVAL_MS = 10000;
// Silence threshold (RMS linear) — below this we classify a sample as silent.
const SILENCE_THRESHOLD = 0.01;

function rmsOfTimeDomain(buf) {
  let sum = 0;
  for (let i = 0; i < buf.length; i++) {
    const v = (buf[i] - 128) / 128;
    sum += v * v;
  }
  return Math.sqrt(sum / buf.length);
}

// Cheap pitch via autocorrelation on the time-domain buffer.
function autocorrelatePitch(buf, sampleRate) {
  const SIZE = buf.length;
  const normalized = new Float32Array(SIZE);
  for (let i = 0; i < SIZE; i++) normalized[i] = (buf[i] - 128) / 128;
  let bestOffset = -1, bestCorr = 0;
  const minPeriod = Math.floor(sampleRate / 500); // 500 Hz max
  const maxPeriod = Math.floor(sampleRate / 70);  // 70 Hz min
  for (let offset = minPeriod; offset <= maxPeriod; offset++) {
    let corr = 0;
    for (let i = 0; i < SIZE - offset; i++) corr += normalized[i] * normalized[i + offset];
    corr /= (SIZE - offset);
    if (corr > bestCorr) { bestCorr = corr; bestOffset = offset; }
  }
  if (bestOffset <= 0 || bestCorr < 0.01) return 0;
  return sampleRate / bestOffset;
}

export function useVoiceCapture({
  actor,          // 'coach' | 'guide' | 'playerB' | 'conflict'
  roomCode,
  isDemoRoom = false,
  autoStart = false,
  activeRound = true,  // when false, recording is force-stopped + state → idle
}) {
  const [state, setState] = useState('idle');
  const [error, setError] = useState(null);
  const [baseline, setBaseline] = useState(null);

  const audioCtxRef = useRef(null);
  const streamRef = useRef(null);
  const analyserRef = useRef(null);
  const bufRef = useRef(null);
  const sampleTimerRef = useRef(null);
  const flushTimerRef = useRef(null);
  const recognitionRef = useRef(null);

  const sampleBufRef = useRef([]);     // pending voice samples
  const transcriptBufRef = useRef([]); // pending transcript lines

  const basePath = isDemoRoom
    ? `/api/olg/admin/demo-room/${roomCode}`
    : `/api/olg/rooms/${roomCode}`;

  const flush = useCallback(async () => {
    const s = sampleBufRef.current.splice(0, sampleBufRef.current.length);
    const t = transcriptBufRef.current.splice(0, transcriptBufRef.current.length);
    const H = { 'Content-Type': 'application/json', ...authHeader() };
    try {
      if (s.length) {
        await fetch(`${API}${basePath}/voice-samples`, {
          method: 'POST', headers: H,
          body: JSON.stringify({ samples: s }),
        });
      }
      if (t.length) {
        await fetch(`${API}${basePath}/transcript`, {
          method: 'POST', headers: H,
          body: JSON.stringify({ lines: t }),
        });
      }
    } catch (_) {
      // Push back on failure so we retry.
      sampleBufRef.current.unshift(...s);
      transcriptBufRef.current.unshift(...t);
    }
  }, [basePath]);

  const takeSample = useCallback(() => {
    const analyser = analyserRef.current;
    const audio = audioCtxRef.current;
    if (!analyser || !audio) return null;
    const buf = bufRef.current;
    analyser.getByteTimeDomainData(buf);
    const rms = rmsOfTimeDomain(buf);
    const activity = rms >= SILENCE_THRESHOLD ? 'speaking' : 'silent';
    const loudness_db = rms > 0 ? Math.round(20 * Math.log10(rms) * 10) / 10 : -80;
    const pitch_hz = activity === 'speaking'
      ? Math.round(autocorrelatePitch(buf, audio.sampleRate))
      : 0;
    return { loudness_db, pitch_hz, activity };
  }, []);

  const startRecognition = useCallback(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;
    try {
      const rec = new SR();
      rec.continuous = true;
      rec.interimResults = false;
      rec.lang = 'en-US';
      rec.onresult = (ev) => {
        for (let i = ev.resultIndex; i < ev.results.length; i++) {
          const r = ev.results[i];
          if (!r.isFinal) continue;
          const transcript = r[0].transcript.trim();
          if (!transcript) continue;
          transcriptBufRef.current.push({
            ts: new Date().toISOString(),
            actor,
            text: transcript,
            confidence: r[0].confidence || null,
          });
        }
      };
      rec.onerror = () => { /* swallow — SR is best-effort */ };
      rec.onend = () => {
        // Chrome auto-stops after a while; restart if we're still recording.
        if (state === 'recording' && recognitionRef.current === rec) {
          try { rec.start(); } catch (_) { /* already started */ }
        }
      };
      rec.start();
      recognitionRef.current = rec;
    } catch (e) {
      // Best-effort — transcript may not be available on this browser.
    }
  }, [actor, state]);

  const stopRecognition = useCallback(() => {
    const rec = recognitionRef.current;
    if (rec) {
      try { rec.onend = null; rec.stop(); } catch (_) {}
      recognitionRef.current = null;
    }
  }, []);

  const requestMic = useCallback(async () => {
    if (!navigator.mediaDevices || !window.AudioContext) {
      setState('unsupported');
      return false;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: false },
      });
      streamRef.current = stream;
      const Ctx = window.AudioContext || window.webkitAudioContext;
      const ctx = new Ctx();
      audioCtxRef.current = ctx;
      const src = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 2048;
      src.connect(analyser);
      analyserRef.current = analyser;
      bufRef.current = new Uint8Array(analyser.fftSize);
      // Baseline capture is now optional — jump straight to 'ready' so the
      // first tap of the chip can start recording immediately. The player can
      // still trigger a baseline later via `captureBaseline()` if desired.
      setState('ready');
      return true;
    } catch (e) {
      setError(e.message || 'Mic denied');
      setState('denied');
      return false;
    }
  }, []);

  const captureBaseline = useCallback(async () => {
    // 3-second rolling average of RMS + pitch while the user reads a
    // neutral sentence. Posted to the server as the player's baseline.
    if (!analyserRef.current) {
      const ok = await requestMic();
      if (!ok) return null;
    }
    const samples = [];
    const start = Date.now();
    return new Promise((resolve) => {
      const tick = () => {
        const s = takeSample();
        if (s && s.activity === 'speaking') samples.push(s);
        if (Date.now() - start >= 3000) {
          const loud = samples.length
            ? samples.reduce((a, b) => a + b.loudness_db, 0) / samples.length
            : null;
          const pitch = samples.filter((x) => x.pitch_hz > 0);
          const pitch_hz = pitch.length
            ? pitch.reduce((a, b) => a + b.pitch_hz, 0) / pitch.length
            : null;
          const payload = {
            actor,
            loudness_db: loud != null ? Math.round(loud * 10) / 10 : null,
            pitch_hz: pitch_hz != null ? Math.round(pitch_hz) : null,
            pace_wpm: null,
          };
          const H = { 'Content-Type': 'application/json', ...authHeader() };
          fetch(`${API}${basePath}/voice-baseline`, {
            method: 'POST', headers: H,
            body: JSON.stringify(payload),
          }).catch(() => {});
          setBaseline(payload);
          setState('ready');
          resolve(payload);
          return;
        }
        setTimeout(tick, 100);
      };
      tick();
    });
  }, [actor, basePath, requestMic, takeSample]);

  const start = useCallback(async () => {
    if (state === 'idle') {
      const ok = await requestMic();
      if (!ok) return;
    }
    sampleTimerRef.current = setInterval(() => {
      const s = takeSample();
      if (!s) return;
      sampleBufRef.current.push({
        ts: new Date().toISOString(),
        actor,
        ...s,
        overlap: false, // true-overlap detection needs multi-mic correlation
      });
    }, SAMPLE_INTERVAL_MS);
    flushTimerRef.current = setInterval(flush, FLUSH_INTERVAL_MS);
    startRecognition();
    setState('recording');
  }, [actor, flush, requestMic, startRecognition, state, takeSample]);

  const stop = useCallback(async () => {
    clearInterval(sampleTimerRef.current); sampleTimerRef.current = null;
    clearInterval(flushTimerRef.current); flushTimerRef.current = null;
    stopRecognition();
    await flush();
    setState((prev) => (prev === 'recording' ? 'ready' : prev));
  }, [flush, stopRecognition]);

  // Called when the round boundary moves — flushes, stops, releases the mic,
  // and forces the chip back to its "Enable voice" state so recording must
  // be opted into for every round.
  const resetForNewRound = useCallback(async () => {
    clearInterval(sampleTimerRef.current); sampleTimerRef.current = null;
    clearInterval(flushTimerRef.current); flushTimerRef.current = null;
    stopRecognition();
    await flush();
    try { streamRef.current?.getTracks().forEach((t) => t.stop()); } catch (_) {}
    streamRef.current = null;
    try { audioCtxRef.current?.close(); } catch (_) {}
    audioCtxRef.current = null;
    analyserRef.current = null;
    bufRef.current = null;
    setState('idle');
  }, [flush, stopRecognition]);

  // Cleanup on unmount.
  useEffect(() => () => {
    try { stop(); } catch (_) {}
    try { streamRef.current?.getTracks().forEach((t) => t.stop()); } catch (_) {}
    try { audioCtxRef.current?.close(); } catch (_) {}
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Round boundary — the parent flips `activeRound` to false when the round
  // completes OR when the debrief overlay opens. We release the mic so the
  // player must opt-in again for the next round.
  useEffect(() => {
    if (!activeRound) {
      resetForNewRound();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeRound]);

  useEffect(() => {
    if (autoStart && state === 'idle') { requestMic(); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoStart]);

  return { state, baseline, error, start, stop, captureBaseline, requestMic, resetForNewRound };
}

export default useVoiceCapture;
